import styled from '@emotion/styled';
import classNames from 'classnames';
import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useCallbackRef } from 'use-callback-ref';

import { EvtKeys } from 'app/types';

import styles, { BorderRadius } from '../../constants/styles';
import { useLockBodyScroll } from '../../hooks';
import { getColorVariable } from '../../theme/colors';
import BodyEnd from '../BodyEnd';
import ClickAwayListener from '../EventListeners/ClickAwayListener';
import KeyCodeListener from '../EventListeners/KeyCodeListener';
import { Coords } from '../utils/lib/dom';

// Local Typings
type AnchorPosition = 'bottom' | 'top';
interface PopoverContainerProps {
  align?: 'left' | 'right';
  anchor?: AnchorPosition;
  borderRadius?: BorderRadius;
  coverAnchorEl?: boolean;
  maxHeight?: number;
  noPaddingY?: boolean;
  offset?: number;
}
interface StyleProps {
  boundingClientRect: DOMRect;
  isVisible: boolean;
  left?: number;
}
export interface MenuProps extends PopoverContainerProps {
  anchorEl: HTMLElement;
  className?: string;
  closeOnTab?: boolean;
  isOpen: boolean;
  lockScrollElements?: (HTMLElement | null)[];
  onClose?: () => void;
  withBodyEnd?: boolean;
}
interface GetPositionProps {
  align?: PopoverContainerProps['align'];
  boundingClientRect: DOMRect;
  placement: AnchorPosition;
}

// Local Variables
function getMenuPosition({
  boundingClientRect,
  placement,
}: GetPositionProps): Coords & { isInYView: boolean } {
  const {
    height,
    width,
  } = boundingClientRect;

  const windowHeight = window.innerHeight;

  let top = height && placement === 'top'
    ? boundingClientRect.top + height / 2
    : boundingClientRect.top;
  const left = width && boundingClientRect.left
    ? boundingClientRect.left + width / 2
    : boundingClientRect.left;

  if (placement === 'bottom') {
    top = boundingClientRect.bottom;
  } else if (placement === 'top') {
    top = boundingClientRect.top;
  }

  const isInYView = placement === 'top'
    ? height + top < windowHeight
    : boundingClientRect.top > 0;

  return {
    isInYView,
    left,
    top,
  };
}

const PopoverContainer = styled.div<PopoverContainerProps & StyleProps>(({
  anchor = 'top',
  borderRadius = 'sm',
  coverAnchorEl,
  maxHeight = window.innerHeight * 0.8, // 80vh
  noPaddingY = false,
  offset = 0,
  theme,
  ...props
}) => {
  const positionProps = anchor === 'top'
    ? {
      top: props.boundingClientRect.top + (coverAnchorEl ? 0 : props.boundingClientRect.height) + offset,
    }
    : {
      bottom: window.innerHeight - props.boundingClientRect.top - props.boundingClientRect.height + offset,
    };

  function getMaxHeightForScreen() {
    if (positionProps.top !== undefined) {
      const diff = window.innerHeight - positionProps.top;

      return diff > positionProps.top ? diff : positionProps.top;
    }

    if (positionProps.bottom !== undefined) {
      const diff = window.innerHeight - positionProps.bottom;

      return diff > positionProps.bottom ? diff : positionProps.bottom;
    }

    return maxHeight;
  }

  const greatestMaxHeightAvailable = getMaxHeightForScreen() - 24;

  const smallestMaxHeight = maxHeight < greatestMaxHeightAvailable ? maxHeight : greatestMaxHeightAvailable;

  return {
    backgroundColor: getColorVariable('black0'),
    border: theme.mode === 'dark' ? `2px solid ${theme.semanticColors.input_border_default}` : undefined,
    borderRadius: styles.borderRadius[borderRadius],
    boxShadow: theme.mode === 'dark' ? undefined : styles.boxShadow.default,
    left: props.align === 'right' ? undefined : props.boundingClientRect.left,
    maxHeight: smallestMaxHeight,
    minWidth: 'fit-content',
    overflowY: 'auto',
    padding: noPaddingY ? undefined : `${styles.padding.xs} 0`,
    position: 'fixed',
    right: props.align === 'right' ? window.innerWidth - props.boundingClientRect.right : undefined,
    visibility: props.isVisible ? 'visible' : 'hidden',
    width: props.boundingClientRect.width,
    zIndex: styles.zIndex.modal,

    ...positionProps,
  };
});

// Component Definition
const Popover: FC<PropsWithChildren<MenuProps>> = ({
  align,
  anchor,
  anchorEl,
  borderRadius,
  children,
  className,
  closeOnTab = true,
  coverAnchorEl,
  isOpen,
  lockScrollElements,
  maxHeight,
  noPaddingY,
  offset,
  onClose,
  withBodyEnd,
}) => {
  const [position, setPosition] = useState<AnchorPosition>(anchor ?? 'top');
  const [popoverContainerEl, setPopoverContainerEl] = useState<HTMLDivElement | null>(null);
  const [hasPositionChanged, setHasPositionChanged] = useState(false);
  const [boundingRect, setBoundingRect] = useState<DOMRect>(anchorEl.getBoundingClientRect());

  const popoverContainerRef = useCallbackRef<HTMLDivElement>(null,
    setPopoverContainerEl);

  const handleTab = useCallback((evt: KeyboardEvent) => {
    if (closeOnTab) {
      evt.preventDefault();
      onClose?.();
    }
  },
  [closeOnTab, onClose]);

  const handleClose = useCallback(() => {
    onClose?.();
  },
  [onClose]);

  const flipY = useCallback(() => {
    if (position === 'top') {
      setPosition('bottom');
    } else {
      setPosition('top');
    }

    setHasPositionChanged(true);
  },
  [position]);

  const boundingClientRect = boundingRect;

  const menuPosition = popoverContainerEl
    ? getMenuPosition({
      boundingClientRect: popoverContainerEl.getBoundingClientRect(),
      placement: anchor ?? 'top',
    })
    : null;

  useLockBodyScroll({
    elements: lockScrollElements,
    isLocked: isOpen,
  });

  useEffect(() => {
    setPopoverContainerEl(popoverContainerRef.current);
  },
  [popoverContainerRef]);

  useEffect(() => {
    if (menuPosition && !menuPosition.isInYView && !hasPositionChanged) {
      flipY();
    }
  },
  [flipY, hasPositionChanged, menuPosition]);

  useEffect(() => {
    if (!isOpen) {
      setHasPositionChanged(false);
    }
  },
  [isOpen]);

  useEffect(() => {
    const handleResize = () => {
      setBoundingRect(anchorEl.getBoundingClientRect());
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  },
  [anchorEl]);

  if (!isOpen) {
    return null;
  }

  const content = (
    <KeyCodeListener
      keyCodeActions={[
        {
          action: handleClose,
          key: EvtKeys.Esc,
        },
        ...(closeOnTab
          ? [{
            action: handleTab,
            key: EvtKeys.Tab,
          }]
          : []),
      ]}
    >
      <ClickAwayListener onClickOutside={handleClose}>
        <div className={classNames(className, 'popover')}>
          <PopoverContainer
            align={align}
            anchor={position}
            borderRadius={borderRadius}
            boundingClientRect={boundingClientRect}
            className="popover_popover-container"
            coverAnchorEl={coverAnchorEl}
            data-testid="list-container"
            isVisible={!!menuPosition?.isInYView || hasPositionChanged}
            left={menuPosition?.left}
            maxHeight={maxHeight}
            noPaddingY={noPaddingY}
            offset={offset}
            ref={popoverContainerRef}
          >
            {children}
          </PopoverContainer>
        </div>
      </ClickAwayListener>
    </KeyCodeListener>
  );

  return withBodyEnd
    ? (
      <BodyEnd>
        {content}
      </BodyEnd>
    )
    : content;
};

// Exports
export default Popover;
